import { RELIC_DEFS } from "../data/balance.js";
import type { RelicEffectType } from "../models/RelicEffect.js";
import { evalStatExpression } from "./evalStatExpression.js";

/**
 * 유물 보유 + 효과 합산 — 순수 로직 (Phaser 비의존, 단위 테스트 대상).
 * 유물별 **누적 보유 개수**를 보관하고 레벨(= 개수)을 산정한다 (idleblade 레퍼런스).
 * 골드 차감은 GameScene이 수행하고, 본 컨트롤러는 정의 검증 + 개수 누적 + 효과 환산만 담당한다.
 * 효과: effectType별 `evalStatExpression(effectExpr, base, level)`를 합산 → 배수로 환산.
 * level = count (1개=Lv1), 다음 레벨 도달 필요 개수 = level+1. 매핑(GameScene 4지점):
 * attackPercent=기본 피해, goldGainPercent=골드 획득, fireRatePercent=발사 간격(감소), projectileSpeedPercent=발사체 속도.
 */
export class RelicController {
  /** relicId → 누적 보유 개수. 미보유는 키 없음(=0). */
  private readonly _counts = new Map<string, number>();

  constructor(initial?: Readonly<Record<string, number>>) {
    if (initial) {
      for (const [id, c] of Object.entries(initial)) {
        // 정의에 없는 relicId·0 이하는 폐기 (데이터 변경/손상 방어).
        const n = Math.floor(c);
        if (id in RELIC_DEFS && n > 0) this._counts.set(id, n);
      }
    }
  }

  /** relicId 누적 보유 개수 (미보유 = 0). */
  count(relicId: string): number {
    return this._counts.get(relicId) ?? 0;
  }

  /** relicId 레벨 (= 누적 보유 개수, 1개=Lv1, 미보유=0). */
  level(relicId: string): number {
    return this.count(relicId);
  }

  /** relicId 보유 여부 (count > 0). */
  owns(relicId: string): boolean {
    return this.count(relicId) > 0;
  }

  /** 다음 레벨 도달에 필요한 총 보유 개수 (= 현재 레벨 + 1). */
  nextLevelRequired(relicId: string): number {
    return this.count(relicId) + 1;
  }

  /** 보유 유물 종류 수 (개수 합 아님). */
  get ownedKindCount(): number {
    return this._counts.size;
  }

  /**
   * 유물 1개 획득 — 정의 존재 시 누적 개수 +1 (무제한, 레벨 +1). 미정의면 무변경 + false.
   * 골드 차감은 호출자(GameScene) 책임 — 본 메서드는 개수만 변경.
   */
  acquire(relicId: string): boolean {
    if (!(relicId in RELIC_DEFS)) return false;
    this._counts.set(relicId, this.count(relicId) + 1);
    return true;
  }

  /** relicId의 현재 레벨 기준 효과값(%) = evalStatExpression(effectExpr, base, level). 미보유=0. */
  effectValueAt(relicId: string): number {
    const def = RELIC_DEFS[relicId];
    const lv = this.count(relicId);
    if (!def || lv <= 0) return 0;
    return evalStatExpression(def.effectExpr, def.base, lv);
  }

  /** attackPercent 합산 반영 기본 피해 배수 (기본 1.0). */
  attackMultiplier(): number {
    return 1 + this.sumPercent("attackPercent") / 100;
  }

  /** goldGainPercent 합산 반영 골드 획득 배수 (기본 1.0). */
  goldMultiplier(): number {
    return 1 + this.sumPercent("goldGainPercent") / 100;
  }

  /** projectileSpeedPercent 합산 반영 발사체 속도 배수 (기본 1.0). */
  speedMultiplier(): number {
    return 1 + this.sumPercent("projectileSpeedPercent") / 100;
  }

  /**
   * fireRatePercent 합산 반영 발사 간격 배수 (기본 1.0, 감소 방향).
   * 쿨타임 단축이므로 (1 − 합산%/100). 과도 단축 방어로 0.1 하한.
   */
  fireRateMultiplier(): number {
    return Math.max(0.1, 1 - this.sumPercent("fireRatePercent") / 100);
  }

  /**
   * 보유 유물 중 effectType이 일치하는 효과값(레벨 반영) 합 (미보유/없음 = 0). UI·dump 표시 공유.
   * 각 유물 effectValue = evalStatExpression(effectExpr, base, level) — 레벨↑ 시 효과↑.
   */
  sumPercent(effectType: RelicEffectType): number {
    let total = 0;
    for (const [id, lv] of this._counts) {
      const def = RELIC_DEFS[id];
      if (def && def.effectType === effectType && lv > 0) {
        total += evalStatExpression(def.effectExpr, def.base, lv);
      }
    }
    return total;
  }

  /** 보유 유물 개수 맵 (저장용). relicId → 누적 개수 (>0만). */
  toState(): Record<string, number> {
    return Object.fromEntries(this._counts);
  }
}
